/**
 * CommandPalette — the global search (Cmd+K / Ctrl+K)
 * ===================================================
 * Usability review of 23 October 2026, item 2. Opened from the sidebar, the
 * top bar or the keyboard on any page; type a company, a ticker, a deal or a
 * page name; Enter goes to the best match, and each deal also offers its
 * ledger and its positions.
 *
 * What it searches is read once per session from the deals, securities and
 * investor lists, plus the positions from the last NAV this browser read, and
 * kept in the same cache the pages use, so the second opening is instant.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { readCache, writeCache, cacheKey } from "./useCachedGet";
import { buildIndex, search } from "./searchIndex";
import "./CommandPalette.css";

const INDEX_KEY = "search-index";
const STALE_MS = 10 * 60 * 1000;

// Every page of a paginated list endpoint.
async function listAll(url) {
  let next = url;
  let rows = [];
  for (let guard = 0; next && guard < 40; guard += 1) {
    const res = await api.get(next);
    const body = res.data;
    rows = rows.concat(Array.isArray(body) ? body : body.results || []);
    next = Array.isArray(body) ? null : body.next;
    // `next` is absolute; the client's base already carries /api/v1.
    if (next) next = next.replace(/^https?:\/\/[^/]+\/api\/v1/, "");
  }
  return rows;
}

async function fetchSources() {
  const [deals, securities, investors] = await Promise.all([
    listAll("/securities/deals/").catch(() => []),
    listAll("/securities/securities/").catch(() => []),
    listAll("/investors/lp-accounts/").catch(() => []),
  ]);
  return { deals, securities, investors };
}

export default function CommandPalette({ open, onClose, pages }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [sources, setSources] = useState(() => readCache(INDEX_KEY)?.data || null);
  const [loading, setLoading] = useState(false);
  const input = useRef(null);
  const list = useRef(null);

  // Load (or refresh) what can be searched the first time the box opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    setTimeout(() => input.current && input.current.focus(), 0);
    const cached = readCache(INDEX_KEY);
    if (cached && Date.now() - cached.savedAt < STALE_MS) return;
    let live = true;
    setLoading(true);
    fetchSources()
      .then((data) => {
        if (!live) return;
        writeCache(INDEX_KEY, data);
        setSources(data);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [open]);

  const entries = useMemo(() => {
    // The positions come from the last NAV the Dashboard or NAV page read, so
    // they cost nothing here and are as fresh as the page the reader came from.
    const nav = readCache(cacheKey("/market-data/nav/"))?.data;
    return buildIndex({ pages, ...(sources || {}), positions: nav?.positions || [] });
    // `open` so each opening picks up a NAV read since the last one.
  }, [pages, sources, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const results = useMemo(() => {
    if (!query.trim()) return entries.filter((e) => e.kind === "Page").slice(0, 8);
    return search(entries, query);
  }, [entries, query]);

  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    const row = list.current && list.current.querySelector(`[data-index="${active}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  const go = (to) => {
    onClose();
    navigate(to);
  };
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[active]) {
      e.preventDefault();
      go(results[active].to);
    }
  };

  let lastKind = null;
  return (
    <div className="cmdk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Search">
        <div className="cmdk-input-row">
          <span aria-hidden="true" className="cmdk-glass">⌕</span>
          <input
            ref={input}
            className="cmdk-input"
            placeholder="Search deals, tickers, positions, investors, pages…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Search"
            aria-controls="cmdk-results"
            aria-activedescendant={results[active] ? `cmdk-${active}` : undefined}
            role="combobox"
            aria-expanded="true"
          />
          <kbd className="cmdk-esc">Esc</kbd>
        </div>
        <div className="cmdk-results" id="cmdk-results" role="listbox" ref={list}>
          {!query.trim() && <div className="cmdk-heading">Jump to</div>}
          {results.map((r, i) => {
            const heading = query.trim() && r.kind !== lastKind ? r.kind : null;
            lastKind = r.kind;
            return (
              <React.Fragment key={r.key}>
                {heading && <div className="cmdk-heading">{heading === "Security" ? "Securities" : `${heading}s`}</div>}
                <div
                  id={`cmdk-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={i === active}
                  className={`cmdk-item${i === active ? " active" : ""}`}
                  onMouseMove={() => setActive(i)}
                  onClick={() => go(r.to)}
                >
                  <span className={`cmdk-kind cmdk-kind-${r.kind.toLowerCase()}`}>{r.kind}</span>
                  <span className="cmdk-text">
                    <span className="cmdk-label">{r.label}</span>
                    {r.sub && <span className="cmdk-sub">{r.sub}</span>}
                  </span>
                  {r.extra.length > 0 && (
                    <span className="cmdk-extra">
                      {r.extra.map((x) => (
                        <button
                          key={x.label}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            go(x.to);
                          }}
                        >
                          {x.label}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
              </React.Fragment>
            );
          })}
          {query.trim() && !results.length && (
            <div className="cmdk-empty">
              {loading ? "Loading what can be searched…" : `Nothing matches “${query.trim()}”. Searched deals, positions, securities, investors and pages.`}
            </div>
          )}
        </div>
        <div className="cmdk-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> to move · <kbd>Enter</kbd> to open · <kbd>Esc</kbd> to close</span>
          {loading && <span>Updating the list…</span>}
        </div>
      </div>
    </div>
  );
}
